import React, { createContext, useContext, useMemo } from 'react';
import { getCollectiveFromDomain } from '../common/domainMap';
import { getVertical } from '../common/verticals';

/**
 * CollectiveContext
 *
 * When the app is accessed via a dedicated domain (e.g. goodyear-events.com),
 * `collective` is set and the app renders in single-collective mode.
 *
 * When accessed via the main domain, `collective` is null and the full
 * multi-city site renders as normal.
 *
 * Shape of `collective`:
 *   { citySlug, vertical, label, v }
 *   where `v` is the full vertical config object from verticals.js
 */
const CollectiveContext = createContext(null);

export function CollectiveProvider({ children }) {
  const collective = useMemo(() => {
    const domain = getCollectiveFromDomain();
    if (!domain) return null;
    return {
      ...domain,
      v: getVertical(domain.vertical),
    };
  }, []);

  return (
    <CollectiveContext.Provider value={collective}>
      {children}
    </CollectiveContext.Provider>
  );
}

/** Returns the collective config, or null on the main site. */
export function useCollective() {
  return useContext(CollectiveContext);
}

/** Returns true when the app is running in single-collective mode. */
export function useIsCollectiveMode() {
  return useContext(CollectiveContext) !== null;
}
